import { useId } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { Svg, SvgBubble } from '@hopskipdrive/react-components';
import { RadioGroup } from '@mui/material';
import { analyticsRiderServicesFieldChanged } from '@rideiq/api/services/Analytics';
import styled from 'styled-components';

import { useLiveServices } from '../../../hooks/useLiveServices';
import type { RiderFormValues } from '../../../types';
import { parseServiceConflicts } from '../../../utils/parseServiceConflicts';
import { RadioOptionCard } from '../primitives/RadioOptionCard';

import { SubsectionHeaderRow, SubsectionKbLink, SubsectionLabel, SubsectionWrapper } from './styles';

const KB_URL = 'https://help.hopskipdrive.com/hc/en-us/articles/48982172524052';

const RECORDING_PREFIX = 'Ride Recording: ';
const isRecordingService = (name: string) => name.startsWith(RECORDING_PREFIX);

export const InRideRecordingSubsection = () => {
  const labelId = useId();
  const { setValue, control } = useFormContext<RiderFormValues>();
  const services = useWatch({ control, name: 'services' }) ?? [];
  const liveServicesState = useLiveServices();
  const { slug } = useParams<{ slug?: string }>();
  const allServices = liveServicesState.status === 'ready' ? liveServicesState.services : [];
  const recordingServices = allServices.filter((s) => isRecordingService(s.name));
  const recordingCodes = new Set(recordingServices.map((s) => s.code));
  const serviceConflictMessages = useWatch({ control, name: 'serviceConflictMessages' }) ?? [];
  const conflictsByName = parseServiceConflicts(serviceConflictMessages);

  const clearRecording = () => {
    const clearedCodes = services.filter((c) => recordingCodes.has(c));

    setValue(
      'services',
      services.filter((c) => !recordingCodes.has(c)),
      { shouldDirty: true }
    );
    clearedCodes.forEach((code) => {
      analyticsRiderServicesFieldChanged({ field_name: code, new_value: false, rider_id: slug ?? null });
    });
  };

  const select = (code: string) => {
    const previousCode = services.find((c) => recordingCodes.has(c));
    const without = services.filter((c) => !recordingCodes.has(c));

    setValue('services', [...without, code], { shouldDirty: true });
    if (previousCode && previousCode !== code) {
      analyticsRiderServicesFieldChanged({ field_name: previousCode, new_value: false, rider_id: slug ?? null });
    }
    analyticsRiderServicesFieldChanged({ field_name: code, new_value: true, rider_id: slug ?? null });
  };

  const noneSelected = !recordingServices.some((s) => services.includes(s.code));

  return (
    <SubsectionWrapper data-testid="in-ride-recording-subsection">
      <SubsectionHeaderRow>
        <SubsectionLabel id={labelId}>Ride Recording</SubsectionLabel>
        <SubsectionKbLink href={KB_URL} target="_blank" rel="noopener noreferrer" aria-label="Ride Recording help">
          <SvgBubble>
            <Svg icon="question" />
          </SvgBubble>
        </SubsectionKbLink>
      </SubsectionHeaderRow>
      <CardList aria-labelledby={labelId} value={services.find((code) => recordingCodes.has(code)) ?? 'none'}>
        {recordingServices
          .filter((s) => s.available)
          .map((s) => {
            const conflictingNames = conflictsByName[s.name];

            return (
              <RadioOptionCard
                key={s.code}
                value={s.code}
                label={s.name.replace(RECORDING_PREFIX, '')}
                subtitle={s.shortDetail ?? ''}
                tooltip={s.detail}
                checked={services.includes(s.code)}
                onSelect={() => select(s.code)}
                error={Boolean(conflictingNames?.length)}
                errorMessage={conflictingNames?.length ? `Conflicts with ${conflictingNames.join(', ')}` : undefined}
              />
            );
          })}
        <RadioOptionCard
          value="none"
          label="None"
          subtitle="Do not record rides"
          checked={noneSelected}
          onSelect={clearRecording}
        />
      </CardList>
    </SubsectionWrapper>
  );
};

const CardList = styled(RadioGroup)`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  gap: 12px;
`;
